import { createStyles, makeStyles, withStyles, Theme } from '@material-ui/core/styles';
import { alpha } from '@material-ui/core/styles/colorManipulator';

import BaseStepConnector from '@material-ui/core/StepConnector';
import BaseStep from '@material-ui/core/Step';
import BaseStepLabel from '@material-ui/core/StepLabel';
import BaseStepIcon from '@material-ui/core/StepIcon';
import BaseStepButton from '@material-ui/core/StepButton';

export const StepConnector = withStyles((theme: Theme) => ({
  alternativeLabel: {
    top: 22,
  },
  active: {
    '& $lineVertical': {
      backgroundImage: `linear-gradient(
            180deg,
            ${theme.oysterTheme.oyster} 0%,
            ${theme.oysterTheme.apple} 50%,
            ${theme.oysterTheme.dolphin} 100%
          )`,
    },
    '& $lineHorizontal': {
      backgroundImage: `linear-gradient(
            90deg,
            ${theme.oysterTheme.oyster} 0%,
            ${theme.oysterTheme.apple} 50%,
            ${theme.oysterTheme.dolphin} 100%
          )`,
    },
  },
  completed: {
    '& $line': {
      backgroundColor: theme.oysterTheme.oyster,
    },
  },
  vertical: {
    padding: 0,
    marginLeft: 7,
    position: 'absolute',
    height: '100%',
    top: 15,
    '& $line': {
      width: 2,
      border: 0,
      height: 'calc(100% - 10px)',
    },
  },
  horizontal: {
    padding: 0,
    '& $line': {
      height: 0,
      border: 0,
    },
  },
  line: {
    backgroundColor: theme.oysterTheme.dolphin,
    zIndex: 0,
  },
  lineVertical: {
    backgroundColor: theme.oysterTheme.dolphin,
  },
  lineHorizontal: {
    backgroundColor: theme.oysterTheme.dolphin,
  },
}))(BaseStepConnector);

export const Step = withStyles((theme) => ({
  root: {
    '&:last-child': {
      '& .MuiStepConnector-root': {
        display: 'none',
      },
    },
    '& .step-description': {
      color: theme.oysterTheme.shark,
      fontWeight: 400,
    },
  },
  vertical: {
    position: 'relative',
    minHeight: 25,
    paddingBottom: 25,
  },
  horizontal: {
    paddingLeft: 0,
  },
}))(BaseStep);

export const StepLabel = withStyles((theme) => ({
  root: {
    '& $label': {
      fontWeight: 600,
    },
  },
  vertical: {
    alignItems: 'start',
    textAlign: 'left',
    '& $iconContainer': {
      paddingTop: 5,
    },
    '& $labelContainer': {
      '& span': {
        textAlign: 'left',
      },
    },
  },
  iconContainer: {
    paddingRight: theme.spacing(1.5),
  },
  disabled: {
    '& $labelContainer': {
      '& span': {
        fontWeight: 600,
        color: theme.oysterTheme.obsidian,
      },
    },
  },
  label: {
    color: theme.oysterTheme.obsidian,
  },
  labelContainer: {
    color: theme.oysterTheme.shark,
    fontSize: '0.9rem',
    fontWeight: 400,
    '& span': {
      fontWeight: 600,
      fontSize: '1rem',
    },
  },
}))(BaseStepLabel);

export const StepIcon = withStyles((theme: Theme) => ({
  root: {
    fill: theme.oysterTheme.dolphin,
    fontSize: '1rem',
  },
  active: {
    fill: theme.oysterTheme.oyster,
  },
  completed: {
    fill: theme.oysterTheme.oyster,
  },
}))(BaseStepIcon);

export const StepButton = withStyles((theme: Theme) => ({
  horizontal: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  vertical: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(0.1),
    height: '100%',
  },
}))(BaseStepButton);

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      position: 'relative',
    },
    stepper: {
      padding: 0,
      marginBottom: theme.spacing(0.5),
    },
    indicatorSmall: {
      '& svg': {
        fontSize: '1rem',
      },
    },
    indicatorMedium: {
      '& svg': {
        fontSize: '1.5rem',
      },
      '&$vertical': {
        '& $stepConnector': {
          top: 25,
          '& $stepConnectorLine': {
            marginLeft: theme.spacing(0.5),
            height: 'calc(100% - 20px)',
          },
        },
      },
    },
    indicatorLarge: {
      '& svg': {
        fontSize: '2rem',
      },
      '&$vertical': {
        '& $stepConnector': {
          top: 25,
          '& $stepConnectorLine': {
            marginLeft: theme.spacing(1),
            height: 'calc(100% - 20px)',
          },
        },
      },
    },
    vertical: {
      flexDirection: 'column',
    },
    horizontal: {
      flexDirection: 'row',
    },
    step: {
      display: 'block',
    },
    stepInactive: {
      display: 'block',
    },
    stepActive: {
      paddingBottom: 12,
      '& > span:first-child': {
        paddingBottom: 4,
        borderBottom: `3px solid ${theme.oysterTheme.oyster}`,
      },
    },
    stepConnector: {
      opacity: 1,
    },
    stepConnectorLine: {
      display: 'block',
    },
    stepLabelContainer: {
      display: 'block',
    },
    stepLabelIconContainer: {
      display: 'flex',
    },
    dense: {
      '&$horizontal': {
        '& $step': {
          minHeight: 20,
          padding: 0,
        },
        '& $stepLabelIconContainer': {
          paddingRight: 0,
        },
        '& $stepLabelContainer': {
          display: 'none',
        },
      },
      '&$vertical': {
        '& $step': {
          paddingBottom: 0,
        },
      },
    },
    emphasis: {
      '& $stepInactive': {
        '& $stepLabelContainer': {
          opacity: 0.25,
        },
      },
    },
    activeOnly: {
      '&$horizontal': {
        '& $stepInactive': {
          padding: 0,
          '& $stepLabelIconContainer': {
            paddingRight: 0,
          },
          '& $stepLabelContainer': {
            display: 'none',
          },
        },
      },
      '&$vertical': {
        '& $step': {
          '&:last-child': {
            '& $stepConnector': {
              display: 'block',
            },
          },
        },
        '& $stepInactive': {
          display: 'none',
        },
        '& $stepConnector': {
          top: 0,
          height: 60,
          '& $stepConnectorLine': {
            backgroundColor: 'transparent',
            backgroundImage: `linear-gradient(
              to bottom,
              ${alpha(theme.oysterTheme.oyster, 1)},
              ${alpha(theme.oysterTheme.dolphin, 0)} 80%
            )`,
          },
        },
      },
    },
    heading: {
      marginBottom: theme.spacing(0.5),
      '& .description': {
        color: theme.oysterTheme.shark,
      },
    },
    denseTitle: {
      fontWeight: 600,
    },
  }),
);

export default useStyles;
